import { Button, Link, cn } from "@heroui/react";
import { AnimatePresence, m } from "framer-motion";
import { useState, type KeyboardEvent } from "react";
import { useTrialSignupHref } from "../../../lib/signupUrl";
import InertIcon from "./InertIcon";
import { resolveDemoIcon } from "../icons/defaultIcons";
import type { InboxDemoIcons } from "../data/types";

const TRIAL_PROMPT_COPY = "👋 很高兴见到您！完成注册后，即可免费体验 3Chat。";

type MessageComposerProps = {
  icons: InboxDemoIcons;
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onUserIntent: () => void;
};

export default function MessageComposer({
  icons,
  draft,
  onDraftChange,
  onSend,
  onUserIntent,
}: MessageComposerProps) {
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);
  const canSend = draft.trim().length > 0;
  const trialHref = useTrialSignupHref("inbox_demo");

  const handleSend = () => {
    if (!canSend) return;
    onUserIntent();
    onSend();
    setShowTrialPrompt(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex shrink-0 flex-col px-3 md:px-6">
      <AnimatePresence initial={false}>
        {showTrialPrompt ? (
          <m.div
            animate={{ height: 36, opacity: 1 }}
            className="overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          />
        ) : null}
      </AnimatePresence>

      <div className="relative">
        <AnimatePresence>
          {showTrialPrompt ? (
            <m.div
              animate={{ opacity: 1 }}
              aria-live="polite"
              className="absolute inset-x-0 bottom-[calc(100%-24px)] z-0 flex h-[60px] items-start gap-2.5 overflow-hidden rounded-t-2xl px-4 py-2"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              role="status"
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <img
                alt=""
                aria-hidden
                className="pointer-events-none absolute inset-0 size-full rounded-t-2xl object-cover"
                height={60}
                src="/images/inbox-demo/trial-prompt-bg.png"
                width={587}
              />
              <p className="relative min-w-0 flex-1 font-pingfang text-xs font-normal leading-5 text-default-900">
                {TRIAL_PROMPT_COPY}
              </p>
              <Link
                aria-label="立即试用"
                className="relative shrink-0 rounded-lg border border-[rgba(17,17,17,0.15)] bg-white/95 px-2 py-0.5 font-pingfang text-xs font-normal leading-4 text-black"
                href={trialHref}
                onClick={(event) => event.stopPropagation()}
              >
                立即试用
              </Link>
            </m.div>
          ) : null}
        </AnimatePresence>

        <div className="relative z-10 flex flex-col gap-2 rounded-2xl border border-default-200 bg-content1 p-4 shadow-[0_8px_7.5px_rgba(0,0,0,0.05)] transition-colors duration-150 focus-within:border-[#006FEE]">
          <textarea
            aria-label="消息"
            className="h-12 w-full resize-none bg-transparent text-base leading-6 text-default-900 outline-none placeholder:text-default-400"
            placeholder="请输入消息"
            value={draft}
            onChange={(event) => {
              onUserIntent();
              onDraftChange(event.target.value);
            }}
            onFocus={onUserIntent}
            onKeyDown={handleKeyDown}
          />
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-[12px]">
              <InertIcon className="flex size-8 items-center justify-center rounded-xl text-default-500">
                {resolveDemoIcon(icons, "emoji")}
              </InertIcon>
              <InertIcon className="flex size-8 items-center justify-center rounded-xl text-default-500">
                {resolveDemoIcon(icons, "attachment")}
              </InertIcon>
              <InertIcon className="flex size-8 items-center justify-center rounded-xl text-default-500">
                {resolveDemoIcon(icons, "lightning")}
              </InertIcon>
              <InertIcon className="flex size-8 items-center justify-center rounded-xl text-default-500">
                {resolveDemoIcon(icons, "ai")}
              </InertIcon>
            </div>
            <m.div whileTap={canSend ? { scale: 0.94 } : undefined}>
              <Button
                isIconOnly
                aria-label="Send"
                className={cn(
                  "h-8 min-w-8 rounded-full",
                  canSend
                    ? "bg-[#006FEE] text-white"
                    : "bg-default-300 text-default-500",
                )}
                isDisabled={!canSend}
                radius="full"
                size="sm"
                onPress={handleSend}
              >
                {resolveDemoIcon(icons, "send")}
              </Button>
            </m.div>
          </div>
        </div>
      </div>
      <p className="flex items-center justify-center gap-1 py-2 text-xs font-semibold leading-4 text-default-300">
        POWERED BY 3Chat.ai
      </p>
    </div>
  );
}
